import { useState, useEffect, useContext, useRef } from 'react';
import { Box, Flex, Spinner, Checkbox, Text } from '@chakra-ui/react';
import InfiniteScroll from 'react-infinite-scroller';
import ContentHeader from '../ContentHeader';
import SearchItems from '../SearchItems';
import WatchListItem from '../WatchListItem';
import { SavedItemsService } from '../../services';

import { GlobalContext } from '../../context/global.context';
import style from './index.module.css';
import PurchrSelect from '../PurchrSelect';
import HowToGetStarted from '../HowToGetStarted';

const ITEMS_PER_PAGE = 15;

const filterItemDatas = [
  // 'Filter XYZ',
  // 'Filter ABC',
  // 'Filter PQR',
  // 'Filter ASD',
];

const SavedItem = () => {
  const isMobileSize = window.innerWidth <= 425;
  const isTabletSize = window.innerWidth <= 1024;
  const scrollContainerRef = useRef(null);
  // Guards against stale responses when filters change mid-flight.
  const requestIdRef = useRef(0);
  // Skips the debounced refetch effect on the very first render.
  const didMountRef = useRef(false);

  const {
    updateAllTags,
    allSavedItems,
    initializeSavedItems,
    appendSavedItems,
    deleteSavedItem,
    updateSavedItem,
  } = useContext(GlobalContext);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [fetchingSavedItems, setFetchingSavedItems] = useState(false);

  // Server-side filter state
  const [isActivated, setIsActivated] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    () => (localStorage.getItem('search') || '').toLowerCase()
  );
  const [selectedTags, setSelectedTags] = useState([]);

  // Filter-dropdown options: the full distinct tag set from the server
  // (not just loaded pages), so every option maps to real saved-item data.
  const [tagOptions, setTagOptions] = useState([]);

  const hasActiveFilter =
    Boolean(searchQuery) || isActivated || selectedTags.length > 0;

  // Fetch one page with the current filters applied server-side.
  // reset=true replaces the list (first load / filter change);
  // otherwise the page is appended (infinite scroll).
  const fetchItems = async (pageNo, { reset }) => {
    const requestId = ++requestIdRef.current;
    setFetchingSavedItems(true);

    try {
      const result = await SavedItemsService.getPaginated({
        page: pageNo,
        limit: ITEMS_PER_PAGE,
        q: searchQuery,
        isActivated,
        tags: selectedTags.map((t) => t.value),
      });

      // A newer filter/search has superseded this request — drop the result.
      if (requestId !== requestIdRef.current) return;

      if (reset) {
        initializeSavedItems(result.items || []);
      } else {
        appendSavedItems(result.items || []);
      }
      setHasMoreItems(Boolean(result.hasMore));
      setCurrentPage(pageNo);
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error('Error fetching saved items:', error);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setFetchingSavedItems(false);
      }
    }
  };

  const loadMoreSavedItems = () => {
    if (fetchingSavedItems || !hasMoreItems) return;
    fetchItems(currentPage + 1, { reset: false });
  };

  // Initial load + populate the tag dropdown.
  useEffect(() => {
    fetchItems(1, { reset: true });
    localStorage.removeItem('search');

    (async () => {
      try {
        const tags = (await SavedItemsService.getTags()) || [];
        setTagOptions(tags.map((value) => ({ value, label: value })));
        // Also enrich the shared tag list used by WatchListItem's editor.
        updateAllTags(tags);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch from page 1 whenever a filter changes; debounce the text search.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    const handler = setTimeout(() => {
      fetchItems(1, { reset: true });
    }, 300);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, isActivated, selectedTags]);

  const handleDelete = (savedItemId) => {
    deleteSavedItem(savedItemId);
  };

  const handleUpdate = (updatedSavedItem) => {
    updateSavedItem(updatedSavedItem);
  };

  return (
    <Box>
      <ContentHeader
        title='Watch list'
        description='Review all your saved items, set price change alerts'
        headingStyle={{
          color: 'purchr.brown.dark',
          fontSize: { base: '32px', sm: '44px' },
          fontWeight: '700',
        }}
        descriptionStyle={{
          fontWeight: '400',
          fontSize: '14px',
          color: '#C0C0C0',
          display: { base: 'none', md: 'block' },
        }}
      />

      <SearchItems
        filterItemDatas={filterItemDatas}
        handleSearch={(q) => setSearchQuery(q.toLowerCase())}
        rightChildren={
          <PurchrSelect
            value={selectedTags}
            className={style.customCss}
            data={tagOptions}
            onChange={setSelectedTags}
            isMulti
            handleMulti={true}
            isSearchable={false}
            placeholder='Select Tag'
            controlStyle={{
              minHeight: 'auto',
              height: '26px',
              width: isTabletSize ? '248px' : '412px',
              '&:hover': { borderColor: '#b5b5b5' },
            }}
            externalStyle={{
              valueContainer: (provided) => ({
                ...provided,
                maxWidth: isTabletSize ? '186px' : '350px',
                overflowX: 'auto',
                scrollbarWidth: 'thin',
                flexWrap: 'nowrap',
                padding: '0px 8px',
              }),
              indicatorsContainer: (provided) => ({
                ...provided,
                height: '26px',
              }),
              multiValue: (provided) => ({
                ...provided,
                flexShrink: '0',
                fontSize: '12px',
              }),
              placeholder: (provided) => ({
                ...provided,
                fontSize: '12px',
                fontWeight: '600',
                color: '#6C6C6C',
              }),
            }}
          />
        }>
        <Checkbox
          className={style.checkboxRound}
          isChecked={isActivated}
          size='sm'
          fontWeight='700'
          color='purchr.gray.700'
          padding={{ base: '4px 32px 4px 8px', lg: '4px 24px 4px 8px ' }}
          border='0.7px solid'
          borderColor='purchr.gray.300'
          borderRadius='6px'
          spacing='8px'
          boxShadow='0px 0.7px 1.4px 0px rgba(16, 24, 40, 0.05);'
          bg='white'
          height='26px'
          _checked={{ bg: '#4F0A43', color: 'white', size: 'xs' }}
          onChange={() => setIsActivated(!isActivated)}>
          Active
        </Checkbox>
      </SearchItems>

      <Box
        mt={{ base: '8px', sm: '20px' }}
        height={{
          base: `calc(${window.innerHeight}px - 350px)`,
          lg: `calc(${window.innerHeight}px - 300px)`,
        }}
        overflowY='auto'
        style={{ scrollbarWidth: isMobileSize ? 'none' : 'thin' }}
        ref={scrollContainerRef}>
        {fetchingSavedItems && allSavedItems.length === 0 ? (
          <Flex justifyContent='center' alignItems='center' height='100%'>
            <Spinner size='md' />
          </Flex>
        ) : !allSavedItems.length ? (
          hasActiveFilter ? (
            <Flex justifyContent='center' alignItems='center' height='100%'>
              <Text color='purchr.gray.dark' fontWeight='600'>
                No items match your filters.
              </Text>
            </Flex>
          ) : (
            <HowToGetStarted />
          )
        ) : (
          <InfiniteScroll
            loadMore={loadMoreSavedItems}
            hasMore={!fetchingSavedItems && hasMoreItems}
            getScrollParent={() => scrollContainerRef.current}
            threshold={0}
            useWindow={false}
          >
            {allSavedItems.map((savedItem) => (
              <WatchListItem
                key={savedItem._id}
                savedItem={savedItem}
                postDelete={handleDelete}
                postUpdate={handleUpdate}
              />
            ))}
          </InfiniteScroll>
        )}
        {fetchingSavedItems && hasMoreItems && allSavedItems.length > 0 && (
          <Flex my='10px' alignItems='center' justifyContent='center'>
            <Spinner size='md' />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
export default SavedItem;
